import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import SEOHead from '../components/shared/SEOHead';
import { ParallaxSection, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { heroContent, bioclimaticheFeatures } from '../data/mock';
import { pageSEO, generateServiceSchema } from '../utils/seo';
import { Thermometer, Smartphone, Shield, Zap, Sun, Wind, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BioclimatichePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: Sun,
      title: "Controllo della Luce",
      description: "Lamelle orientabili da 0° a 150° per regolare l'illuminazione naturale in ogni momento."
    },
    {
      icon: Wind,
      title: "Ventilazione Naturale",
      description: "Apertura totale delle lamelle per una perfetta circolazione dell'aria."
    },
    {
      icon: Thermometer,
      title: "Efficienza Energetica",
      description: "Riduzione dei costi di climatizzazione grazie alla gestione passiva del calore."
    },
    {
      icon: Shield,
      title: "Resistenza Estrema",
      description: "Strutture certificate per resistere a carichi neve fino a 300 kg/m²."
    },
    {
      icon: Smartphone,
      title: "Domotica Integrata",
      description: "Controllo da smartphone e integrazione con i principali sistemi smart home."
    },
    {
      icon: Zap,
      title: "Sensori Automatici",
      description: "Chiusura automatica in caso di pioggia e apertura con sensore solare."
    }
  ];

  return (
    <Layout>
      <SEOHead
        title={pageSEO.bioclimatiche.title}
        description={pageSEO.bioclimatiche.description}
        keywords={pageSEO.bioclimatiche.keywords}
        schema={generateServiceSchema({
          name: "Pergole Bioclimatiche",
          description: "Installazione pergole bioclimatiche con lamelle orientabili a Pisa e Toscana"
        })}
      />

      {/* Hero Section */}
      <HeroSection
        title="Pergole Bioclimatiche a Pisa e in Toscana"
        subtitle="Strutture con lamelle orientabili per vivere l'outdoor tutto l'anno. Ideali per il clima mediterraneo toscano."
        ctaText="Richiedi una consulenza dedicata"
        ctaLink="/preventivo"
        videoUrl={heroContent.bioclimatiche.videoUrl}
        imageUrl={heroContent.bioclimatiche.imageUrl}
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Pergole Bioclimatiche Pisa
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                L'architettura che si adatta al clima toscano
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                Le <strong>pergole bioclimatiche</strong> rappresentano la sintesi perfetta tra design contemporaneo 
                e tecnologia avanzata. A Pisa e in Toscana, dove il clima mediterraneo regala 
                oltre 250 giorni di sole l'anno, queste strutture permettono di vivere l'outdoor 
                in ogni stagione, proteggendo da sole, pioggia e vento con un sistema di 
                <strong> lamelle orientabili in alluminio</strong>.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Come Funziona
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Lamelle orientabili per ogni situazione
                </h2>
                <div className="space-y-6">
                  {bioclimaticheFeatures.map((feature, index) => (
                    <FadeInOnScroll key={index} delay={index * 100}>
                      <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-sky-500 font-medium text-sm">{index + 1}</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {feature.title}
                          </h3>
                          <p className="text-gray-600 font-light leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </FadeInOnScroll>
                  ))}
                </div>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="relative overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_3b930011-c4cf-4c5a-b240-bf5736a6fba8/artifacts/nhllob71_Gemini_Generated_Image_alcoifalcoifalco.png"
                  alt="Bioclimatica con lamelle orientabili"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="I Vantaggi"
              title="Perché scegliere una bioclimatica"
              description="Un investimento che migliora la qualità della vita e aumenta il valore del tuo immobile."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className="text-center p-8 group hover:bg-gray-50 transition-colors duration-300 rounded-lg"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500 transition-colors duration-300">
                    <benefit.icon className="w-7 h-7 text-sky-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Installation Image - COLLAGE */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  I Nostri Lavori
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900">
                  Eleganza e funzionalità in ogni dettaglio
                </h2>
                <p className="text-gray-600 mt-4 font-light">
                  Le nostre bioclimatiche trasformano gli spazi esterni, di giorno e di notte
                </p>
              </div>
              
              {/* Collage Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Immagine principale grande */}
                <div className="lg:row-span-2 relative overflow-hidden rounded-lg shadow-xl group">
                  <img
                    src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/wcvtp2ka_IMG_6402.jpeg"
                    alt="Pergola bioclimatica installata - Giovanni Tende"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <p className="text-white font-light text-lg">
                      Villa in Toscana - Bioclimatica con vista panoramica
                    </p>
                  </div>
                </div>
                
                {/* Immagine giorno con vetrate */}
                <FadeInOnScroll delay={100}>
                  <div className="relative overflow-hidden rounded-lg shadow-xl group">
                    <img
                      src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/lklbf91x_biocliamtica%20vetrate.png"
                      alt="Bioclimatica con vetrate VEPA - Giorno"
                      className="w-full h-64 lg:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <span className="inline-block px-2 py-1 bg-sky-500 text-white text-xs rounded mb-2">GIORNO</span>
                      <p className="text-white font-light text-sm">
                        Bioclimatica con chiusure VEPA - Spazio vivibile tutto l'anno
                      </p>
                    </div>
                  </div>
                </FadeInOnScroll>
                
                {/* Immagine notte */}
                <FadeInOnScroll delay={200}>
                  <div className="relative overflow-hidden rounded-lg shadow-xl group">
                    <img
                      src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/4kbsc2ob_bioclimatica%20notte.png"
                      alt="Bioclimatica illuminata - Notte"
                      className="w-full h-64 lg:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <span className="inline-block px-2 py-1 bg-amber-500 text-white text-xs rounded mb-2">NOTTE</span>
                      <p className="text-white font-light text-sm">
                        Atmosfera magica con illuminazione LED integrata
                      </p>
                    </div>
                  </div>
                </FadeInOnScroll>
              </div>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Parallax Visual Section */}
      <ParallaxSection
        imageUrl="https://customer-assets.emergentagent.com/job_3b930011-c4cf-4c5a-b240-bf5736a6fba8/artifacts/djjyxus5_Gemini_Generated_Image_12iq0812iq0812iq.png"
        speed={0.3}
        overlayOpacity={0.4}
        minHeight="70vh"
      >
        <div className="h-[70vh] flex items-center">
          <div className="container mx-auto px-6 lg:px-12">
            <ParallaxElement speed={0.15} direction="up" className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
                Un valore aggiunto per la tua proprietà
              </h2>
              <p className="text-lg text-white/80 font-light">
                Le pergole bioclimatiche aumentano il valore immobiliare del 10-15%, 
                creando nuovi spazi vivibili e migliorando l'efficienza energetica dell'edificio.
              </p>
            </ParallaxElement>
          </div>
        </div>
      </ParallaxSection>

      {/* Financing Banner */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '30px 30px'
          }} />
        </div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                  <span className="text-sm font-medium">Finanziamento Disponibile</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6 leading-tight">
                  La tua bioclimatica a<br />
                  <span className="font-semibold">Tasso Zero</span>
                </h2>
                <p className="text-xl text-white/90 font-light mb-6 leading-relaxed">
                  Paga il 50% all'ordine e il resto in 10 comode rate annuali senza interessi. 
                  TAN 0% e TAEG 0%.
                </p>
                <div className="flex items-center gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold">0%</div>
                    <div className="text-sm text-white/70">Interessi</div>
                  </div>
                  <div className="w-px h-12 bg-white/30" />
                  <div className="text-center">
                    <div className="text-4xl font-bold">10</div>
                    <div className="text-sm text-white/70">Rate annuali</div>
                  </div>
                  <div className="w-px h-12 bg-white/30" />
                  <div className="text-center">
                    <div className="text-4xl font-bold">50%</div>
                    <div className="text-sm text-white/70">Acconto</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/10 rounded-xl mb-6">
                  <Shield className="w-8 h-8 text-white" />
                  <div>
                    <p className="font-medium">Alla documentazione ci pensiamo noi</p>
                    <p className="text-sm text-white/70">Gestiamo tutta la pratica per te</p>
                  </div>
                </div>
                <Link to="/giovanni-tende-smart">
                  <button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-4 font-medium transition-colors inline-flex items-center gap-2">
                    Scopri il finanziamento
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Esempio: Bioclimatica da €12.000</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Acconto (50%)</span>
                    <span className="text-xl font-semibold">€ 6.000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Importo finanziato</span>
                    <span className="text-xl font-semibold text-sky-500">€ 6.000</span>
                  </div>
                  <div className="bg-sky-50 -mx-8 px-8 py-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-900 font-medium">Rata annuale</span>
                        <span className="text-gray-500 text-sm block">per 10 anni</span>
                      </div>
                      <span className="text-3xl font-bold text-sky-500">€ 600</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Scopri la bioclimatica perfetta per te"
        description="Ogni progetto è studiato su misura. Richiedi una consulenza personalizzata."
        variant="light"
      />
    </Layout>
  );
};

export default BioclimatichePage;
